import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  CalendarDaysIcon,
  ArrowPathIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { ukSchedulingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ShiftTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    shift_pattern: 'fixed',
    days_of_week: [],
    start_time: '09:00',
    end_time: '17:00',
    break_duration: 60,
    recurrence_type: 'weekly',
    rotation_days: 7,
    is_active: true
  });

  const { user, isAdmin, isManager } = useAuth();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await ukSchedulingAPI.getShiftTemplates();
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching shift templates:', error);
      setError('Failed to load shift templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      setLoading(true);
      const templateData = {
        ...formData,
        company_id: user.companyId,
        created_by: user.id
      };

      await ukSchedulingAPI.createShiftTemplate(templateData);
      await fetchTemplates();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error creating shift template:', error);
      setError('Failed to create shift template');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTemplate = async () => {
    try {
      setLoading(true);
      await ukSchedulingAPI.updateShiftTemplate(editingTemplate.id, formData);
      await fetchTemplates();
      setShowEditModal(false);
      resetForm();
    } catch (error) {
      console.error('Error updating shift template:', error);
      setError('Failed to update shift template');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;

    try {
      setLoading(true);
      await ukSchedulingAPI.deleteShiftTemplate(templateId);
      await fetchTemplates();
    } catch (error) {
      console.error('Error deleting shift template:', error);
      setError('Failed to delete shift template');
    } finally {
      setLoading(false);
    }
  };

  const handleCloneTemplate = async (template) => {
    setFormData({
      name: `${template.name} (Copy)`,
      description: template.description,
      shift_pattern: template.shift_pattern,
      days_of_week: template.days_of_week || [],
      start_time: template.start_time,
      end_time: template.end_time,
      break_duration: template.break_duration,
      recurrence_type: template.recurrence_type,
      rotation_days: template.rotation_days,
      is_active: true
    });
    setShowCreateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description,
      shift_pattern: template.shift_pattern,
      days_of_week: template.days_of_week || [],
      start_time: template.start_time,
      end_time: template.end_time,
      break_duration: template.break_duration,
      recurrence_type: template.recurrence_type,
      rotation_days: template.rotation_days,
      is_active: template.is_active
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      shift_pattern: 'fixed',
      days_of_week: [],
      start_time: '09:00',
      end_time: '17:00',
      break_duration: 60,
      recurrence_type: 'weekly',
      rotation_days: 7,
      is_active: true
    });
    setEditingTemplate(null);
  };

  const handleDayToggle = (day) => {
    const currentDays = formData.days_of_week || [];
    const isSelected = currentDays.includes(day);
    
    if (isSelected) {
      setFormData({
        ...formData,
        days_of_week: currentDays.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        days_of_week: [...currentDays, day]
      });
    }
  };

  const daysOfWeek = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  const shiftPatterns = [
    { value: 'fixed', label: 'Fixed Schedule' },
    { value: 'rotating', label: 'Rotating Shifts' },
    { value: 'flexible', label: 'Flexible Hours' }
  ];

  const recurrenceTypes = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' }
  ];

  const getShiftDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return '0h';
    const start = new Date(`2000-01-01T${startTime}:00`);
    const end = new Date(`2000-01-01T${endTime}:00`);
    const diff = (end - start) / (1000 * 60 * 60);
    return `${diff}h`;
  };

  const renderTemplateModal = (isEdit = false) => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {isEdit ? 'Edit Shift Template' : 'Create Shift Template'}
          </h3>
          <button
            onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            <span className="sr-only">Close</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); isEdit ? handleUpdateTemplate() : handleCreateTemplate(); }}>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="e.g., Standard Office Hours"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Shift Pattern
                </label>
                <select
                  value={formData.shift_pattern}
                  onChange={(e) => setFormData({ ...formData, shift_pattern: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {shiftPatterns.map(pattern => (
                    <option key={pattern.value} value={pattern.value}>
                      {pattern.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Template description..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Working Days
              </label>
              <div className="flex flex-wrap gap-2">
                {daysOfWeek.map(day => (
                  <button
                    key={day.key}
                    type="button"
                    onClick={() => handleDayToggle(day.key)}
                    className={`px-3 py-2 rounded-md text-sm font-medium ${
                      (formData.days_of_week || []).includes(day.key)
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {day.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Time
                </label>
                <input
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Time
                </label>
                <input
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Break Duration (minutes)
                </label>
                <input
                  type="number"
                  min="0"
                  max="480"
                  value={formData.break_duration}
                  onChange={(e) => setFormData({ ...formData, break_duration: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {formData.shift_pattern === 'rotating' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recurrence
                  </label>
                  <select
                    value={formData.recurrence_type}
                    onChange={(e) => setFormData({ ...formData, recurrence_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {recurrenceTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rotation Cycle (days)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    value={formData.rotation_days}
                    onChange={(e) => setFormData({ ...formData, rotation_days: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Template is active
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
            <button
              type="button"
              onClick={() => isEdit ? setShowEditModal(false) : setShowCreateModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (isEdit ? 'Update Template' : 'Create Template')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  if (loading && templates.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shift Templates</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create and manage predefined shift patterns for your organization
          </p>
        </div>
        {(isAdmin() || isManager()) && (
          <div className="mt-4 sm:mt-0">
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary inline-flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              New Template
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
        {templates.map((template) => (
          <div key={template.id} className="bg-white overflow-hidden shadow rounded-lg hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center">
                    <ClockIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {template.name}
                    </h3>
                  </div>
                  {template.description && (
                    <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                      {template.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    template.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {template.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                <div className="flex items-center text-sm text-gray-500">
                  <CalendarDaysIcon className="h-4 w-4 mr-2" />
                  <span className="capitalize">{template.shift_pattern} Pattern</span>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center text-gray-500">
                    <ClockIcon className="h-4 w-4 mr-2" />
                    <span>{template.start_time} - {template.end_time}</span>
                  </div>
                  <span className="text-gray-900 font-medium">
                    {getShiftDuration(template.start_time, template.end_time)}
                  </span>
                </div>

                {template.days_of_week && template.days_of_week.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {daysOfWeek.map(day => (
                      <span
                        key={day.key}
                        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          template.days_of_week.includes(day.key)
                            ? 'bg-primary-100 text-primary-800'
                            : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {day.label}
                      </span>
                    ))}
                  </div>
                )}

                {template.shift_pattern === 'rotating' && (
                  <div className="flex items-center text-sm text-gray-500">
                    <ArrowPathIcon className="h-4 w-4 mr-2" />
                    <span>{template.recurrence_type} rotation ({template.rotation_days} days)</span>
                  </div>
                )}
              </div>
            </div>

            {(isAdmin() || isManager()) && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditTemplate(template)}
                      className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                    >
                      <PencilIcon className="h-4 w-4 inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleCloneTemplate(template)}
                      className="text-gray-600 hover:text-gray-800 text-sm font-medium"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4 inline mr-1" />
                      Clone
                    </button>
                  </div>
                  <button
                    onClick={() => handleDeleteTemplate(template.id)}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    <TrashIcon className="h-4 w-4 inline mr-1" />
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}

        {templates.length === 0 && !loading && (
          <div className="col-span-full">
            <div className="text-center py-12">
              <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No shift templates</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating your first shift template.
              </p>
              {(isAdmin() || isManager()) && (
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn-primary inline-flex items-center"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    New Template
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCreateModal && renderTemplateModal(false)}
      {showEditModal && renderTemplateModal(true)}
    </div>
  );
};

export default ShiftTemplates;